import { GlobalStateController } from "bai-react-global-state";

import { AiImageStyleTypes } from "../../../../common/aiConstants";
import PresentationLibraryController from "../../../controllers/PresentationLibraryController";
import WorkspaceController from "../../../controllers/WorkspaceController";
import AppController from "../../../core/AppController";
import { ds } from "../../../core/models/dataService";
import AiGenerationService from "../../../core/services/aiGeneration";
import { app } from "../../../namespaces";
import { _ } from "../../../vendor";

function refreshThumbnail(model) {
    console.log("Slide model changed", model.id, model.getMetadata()?.isGenerating);
    if (!model.metadata?.ai?.isGenerating) {
        model.off("change", refreshThumbnail);
        console.log("Slide generation complete");
        model.getThumbnailUrl(model.presentation.id);
    }
}

export function parseGeneratedSlideContent(slide) {
    if (typeof slide === "string") {
        return slide;
    }

    let content = "";

    if (slide.slideTitle && slide.slideTitle.length > 0) {
        content += `# ${slide.slideTitle}\n\n`;
    }
    if (slide.title && slide.title.length > 0) {
        content += `**Title:** ${slide.title}\n`;
    }
    if (slide.subtitle && slide.subtitle.length > 0) {
        content += `**Subtitle:** ${slide.subtitle}\n`;
    }
    if (slide.slideContent && slide.slideContent.length > 0) {
        if (Array.isArray(slide.slideContent)) {
            let slideContent = "";
            for (let item of slide.slideContent) {
                if (typeof item === "string") {
                    slideContent += `- ${item}\n`;
                } else {
                    let pairs = [];
                    for (let key in item) {
                        pairs.push(`- ${key}: ${item[key]}`);
                    }
                    slideContent += pairs.join("\n") + "\n";
                }
            }
            content += `${slideContent}\n`;
        } else {
            content += `${slide.slideContent}\n`;
        }
    }
    if (slide.layout && slide.layout.length > 0) {
        content += `**Layout:** ${slide.layout}\n`;
    }
    if (slide.image && slide.image.length > 0) {
        content += `**Image:** ${slide.image}\n`;
    }
    if (slide.conclusion && slide.conclusion.length > 0) {
        content += `**Conclusion:** ${slide.conclusion}\n`;
    }

    if (slide.intent) {
        content += `${slide.intent}\n`;
    }

    // content = marked.parse(content);
    // content = content.replace(/(\n\s*)+/g, "<br/>");

    // content = content.replace(/(<br\/>\s*)+$/g, "");

    return content;
}

function getInitialState() {
    return {
        initialPrompt: null,
        messages: [],
        outline: null,
        outlineHistory: [],
        imageSource: "ai",
        imageStyle: AiImageStyleTypes.MINIMALIST,
        theme: null,
        language: "eng",
        preserveExactText: false,
        files: [],
        highlightedSlides: [],
        webSearch: false,
        isGenerating: false
    };
}

class GeneratePresentationController extends GlobalStateController {
    async loadPrefs() {
        let prefs = JSON.parse(localStorage.getItem("genPresPrefs") ?? "{}");
        let { themeId, language, imageSource, imageStyle, preserveExactText, webSearch } = prefs;

        let theme;
        if (themeId) {
            theme = ds.builtInThemes.get(themeId);
            if (!theme) {
                theme = await WorkspaceController.getUserTheme(themeId, true);
            }
            if (!theme) {
                theme = await WorkspaceController.getSharedTheme(themeId, true);
            }
        }

        if (!theme) {
            theme = ds.builtInThemes.get("minimal");
        }

        this._updateState(state => ({
            ...state,
            theme,
            language: language ?? state.language,
            imageSource: imageSource ?? state.imageSource,
            imageStyle: imageStyle ?? state.imageStyle,
            preserveExactText: preserveExactText ?? state.preserveExactText,
            webSearch: webSearch ?? state.webSearch
        }));
    }

    reset() {
        this._updateState(getInitialState());

        this.loadPrefs();
    }

    resetChat() {
        this._updateState({
            messages: [],
            outline: null,
            outlineHistory: [],
            imageMap: null
        });
    }

    savePrefs() {
        let { language, theme, imageSource, imageStyle, preserveExactText, webSearch } = this._state;

        localStorage.setItem("genPresPrefs", JSON.stringify({
            language,
            themeId: theme.id,
            imageSource,
            imageStyle,
            preserveExactText,
            webSearch
        }));
    }

    get outline() {
        return this._state.outline;
    }

    async setOutline(outline) {
        await this._updateState({
            outline,
            outlineHistory: [...this._state.outlineHistory, outline]
        });
    }

    async addMessage(messsage) {
        this._updateState({
            messages: [...this._state.messages, messsage],
        });
    }

    async addUserMessage(content, files) {
        this._updateState({
            messages: [...this._state.messages, { role: "user", content, files }],
        });
    }

    async addAssistantMessage(content) {
        this._updateState({
            messages: [...this._state.messages, { role: "assistant", content }],
        });
    }

    setTheme(theme) {
        this._updateState({ theme });
    }

    setLanguage(language) {
        this._updateState({ language });
    }

    setImageSource(imageSource) {
        this._updateState({ imageSource });
    }

    setImageStyle(imageStyle) {
        this._updateState({ imageStyle });
    }

    setPreserveExactText(preserveExactText) {
        this._updateState({ preserveExactText });
    }

    toggleWebSearch() {
        this._updateState(state => ({
            ...state,
            webSearch: !state.webSearch
        }));
    }

    async _processChatResponse(response) {
        let {
            response_type,
            hasDesignInstructions,
            message,
            outline,
            highlightSlides,
        } = response;

        if (hasDesignInstructions || response_type === "design-request") {
            this.addMessage({ role: "assistant", content: "Unable to process design instructions" });
            return;
        }

        if ((response_type === "outline" || response_type === "edit") && outline) {
            console.log(outline);

            await this._updateState(state => ({
                ...state,
                files: state.files.map(f => f.hasBeenUsed ? f : ({ ...f, hasBeenUsed: true, hasBeenUsedFor: response_type }))
            }));

            await this.setOutline({ title: outline.title, topic: outline.presentationTopic, slides: outline.slides.map(slide => ({ slidePrompt: parseGeneratedSlideContent(slide) })) });

            if (response_type === "edit" && highlightSlides && highlightSlides.length > 0) {
                this.higlightSlides(highlightSlides);
            }

            if (message) {
                this.addMessage({ role: "assistant", content: message });
            } else if (response_type === "outline") {
                this.addMessage({ role: "assistant", content: "I've generated an outline for you. You can ask me to make changes, add additional slides, or provide feedback..." });
            } else if (response_type === "edit") {
                this.addMessage({ role: "assistant", content: "I've made the requested changes to the outline. You can ask me to make further changes, add additional slides, or provide feedback..." });
            }
            return;
        }

        if ((response_type === "clarification" || response_type === "chat") && message) {
            this.addMessage({ role: "assistant", content: message });
            return;
        }
    }

    higlightSlides(slideIndexes) {
        this._updateState({ highlightedSlides: slideIndexes });
        _.delay(() => {
            this._updateState({ highlightedSlides: [] });
        }, 1000);
    }

    async regenerateOutline() {
        this._updateState({ outline: null, outlineHistory: [], messages: [] });
        await this.sendPrompt(this._state.initialPrompt);
    }

    async sendPrompt(prompt) {
        let { files, messages, outline, webSearch } = this._state;

        files = files.filter(f => !f.hasBeenUsed);

        if (prompt.trim().length === 0 && !files?.length) {
            return;
        }

        this.addUserMessage(prompt, files);

        this._updateState({ isGenerating: true });
        try {
            let response;
            if (!outline) {
                this._updateState({ initialPrompt: prompt });
                response = await AiGenerationService.classifyPresentationPrompt(prompt, messages, files, webSearch);
            } else {
                response = await AiGenerationService.generateChatResponseMessage(prompt, messages, outline, files, webSearch);
            }

            await this._processChatResponse(response);
        } finally {
            this._updateState({ isGenerating: false });
        }
    }

    async generatePresentationFromOutline() {
        let { outline, language, preserveExactText, imageSource, imageStyle, files, webSearch, theme } = this._state;

        this.savePrefs();

        let { files: savedFiles } = await AiGenerationService.saveContextDocuments(files);

        // create a presentation with ai-generation placeholder slides to be generated
        let presentationModel = {
            name: outline.title,
            theme,
            slides: outline.slides.map(slide => ({
                template_id: "ai-generation",
                metadata: {
                    ...slide
                }
            })),
            workspaceId: AppController.workspaceId,
            metadata: {
                wasGeneratedWithAi: true,
                title: outline.title,
                topic: outline.topic,
                prompt: prompt,
                outline: outline.slides,
                imageSource,
                imageStyle,
                language,
                preserveExactText,
                files: savedFiles,
                webSearch
            }
        };
        let presentation = await PresentationLibraryController.createPresentation(presentationModel);
        await presentation.prepareSlides(false);

        // open the presentation in the editor
        AppController.showEditor({ presentationId: presentation.id, presentation, withAiFeedbackSurvey: !!presentation.get("metadata")?.finalPrompt });

        let slide = presentation.slides.at(0);
        await slide.load();
        await this._finalizeSlide(slide, {
            slideType: null,
            slidePrompt: slide.getMetadata().slidePrompt,
            slideIndex: slide.getIndex(),
            imageSource,
            imageStyle,
            language,
            preserveExactText,
            presentationTopic: outline.topic,
        });

        // start generating each slide asynchronously
        await Promise.all(presentation.slides.map(async (slide, idx) => {
            if (idx === 0) return; // first slide is already being processed
            await slide.load();

            await this._finalizeSlide(slide, {
                slideType: null,
                slidePrompt: slide.getMetadata().slidePrompt,
                slideIndex: slide.getIndex(),
                imageSource,
                imageStyle,
                language,
                preserveExactText,
                presentationTopic: outline.topic,
            });
        }));
    }

    async _finalizeSlide(slide, { slidePrompt, slideIndex, slideType, language, imageStyle, preserveExactText, imageSource, files, presentationTopic }) {
        let prompt = {
            slideType: slideType == "auto" ? null : slideType,
            slidePrompt,
            slideIndex,
            language,
            imageStyle,
            preserveExactText,
            imageSource,
            files,
            presentationTopic
        };

        console.log("Generate slide model", prompt);
        slide.trigger("startGeneration");
        slide.on("change", refreshThumbnail);

        await AiGenerationService.finalizeSlide(slide.id, prompt);
    }

    async parseDocument(file) {
        while (this._state.files.some(f => f.name === file.name)) {
            file.name = `_${file.name}`;
        }

        await this._updateState(state => ({
            ...state,
            files: [...state.files, { name: file.name, type: file.type, isProcessing: true }]
        }));

        try {
            let parsedFile = await AiGenerationService.parseDocument(file, this._state.files);

            await this._updateState(state => ({
                ...state,
                files: [...state.files.filter(f => f.name !== parsedFile.name), parsedFile]
            }));
        } catch (err) {
            await this._updateState(state => ({
                ...state,
                files: state.files.filter(f => f.name !== name)
            }));

            throw err;
        }
    }

    async removeFile(name) {
        await this._updateState(state => ({
            ...state,
            files: state.files.filter(f => f.name !== name)
        }));
    }
}

const generatePresentationController = new GeneratePresentationController(getInitialState());

app.generatePresentationController = generatePresentationController;

export default generatePresentationController;

// Helper functions for handling images efficiently
// extractAndReplaceImages(html) {
//     const imageMap = new Map();
//
//     // Parse HTML and find all img tags
//     const parser = new DOMParser();
//     const doc = parser.parseFromString(html, "text/html");
//
//     const images = doc.querySelectorAll("img[src]");
//
//     images.forEach(img => {
//         const src = img.getAttribute("src");
//
//         // Only process base64 data URLs to avoid affecting regular URLs
//         if (src && src.startsWith("data:image/")) {
//             const imageId = uuid();
//
//             // Store the image data with UUID
//             imageMap.set(imageId, {
//                 src: src,
//                 alt: img.getAttribute("alt") || "",
//                 title: img.getAttribute("title") || ""
//             });
//
//             // Replace src with data-image-id and remove src
//             img.setAttribute("data-image-id", imageId);
//             img.removeAttribute("src");
//         }
//     });
//
//     return {
//         html: doc.body.innerHTML,
//         imageMap: Object.fromEntries(imageMap)
//     };
// }

// async generateOutlineFromDocument(document) {
//     const { html, imageMap } = this.extractAndReplaceImages(document);
//     const response = await AiGenerationService.generateOutlineFromDoc(html);
//     const { outline } = response;
//
//     if (outline) {
//         outline.slides.forEach(slide => {
//             slide.images = slide.images?.map(image => {
//                 if (imageMap[image]) {
//                     return imageMap[image]?.src;
//                 } else if (image.startsWith("data:image/png;base64,")) {
//                     return image;
//                 } else if (!image.startsWith("/image-proxy?url=")) {
//                     const originalSrc = image;
//                     return "/image-proxy?url=" + encodeURIComponent(originalSrc);
//                 }
//                 return image;
//             }) || [];
//         });
//         await this._updateState({ outline, imageMap });
//     } else {
//         throw new Error(`Failed to generate outline`);
//     }
// }
